import { useState, useMemo, useEffect, useRef } from "react";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAppContext } from "../context";
import { api } from "../api";
import {
  Package,
  Loader2,
  ChevronDown,
  AlertTriangle,
  Info,
  RefreshCw,
  Clock,
  History,
  Plus,
  Minus,
  Search,
  ExternalLink,
  ArrowUpDown,
  BarChart3,
} from "lucide-react";
import { cn } from "../lib/utils";

interface TierInfo {
  id: number;
  name: string;
  color: string;
  displayOrder: number;
}

interface InventoryItem {
  id: number;
  nftId: number;
  nftMint: string | null;
  name: string;
  image: string | null;
  description: string | null;
  insuredValue: number | null;
  gradingCompany: string | null;
  grade: string | null;
  firstSeenAt: Date;
  lastSeenAt: Date;
  tier: {
    tierId: number;
    tierName: string;
    tierColor: string;
  } | null;
}

interface InventoryResponse {
  items: InventoryItem[];
  syncStatus: {
    lastSyncedAt: Date | null;
    lastSyncStatus: string | null;
    totalItems: number | null;
    nextSyncAt: Date | null;
  } | null;
  tierCounts: Record<string, number>;
  pagination: {
    hasMore: boolean;
    nextCursor: string | null;
    total: number;
  };
}

interface InventoryChange {
  id: number;
  nftId: number;
  name: string;
  insuredValue: number | null;
  changeType: "added" | "removed";
  detectedAt: Date;
  tier: {
    tierId: number;
    tierName: string;
    tierColor: string;
  } | null;
}

interface ChangesResponse {
  changes: InventoryChange[];
  pagination: {
    hasMore: boolean;
    nextCursor: string | null;
  };
}

interface MachineStatsResponse {
  machine: {
    id: number;
    code: string;
    name: string;
  };
  tiers?: TierInfo[];
}

interface TierValueStats {
  tierId: number;
  tierName: string;
  tierColor: string;
  count: number;
  minValue: number | null;
  maxValue: number | null;
  avgValue: number | null;
  medianValue: number | null;
  totalValue: number | null;
  percentOfTotal: number;
  clusterWeightedAvg: number | null;
  histogram: HistogramBucket[];
}

interface HistogramBucket {
  min: number;
  max: number;
  count: number;
  label: string;
  tierColor?: string;
  tierName?: string;
}

interface ValueStatsResponse {
  overall: {
    count: number;
    minValue: number;
    maxValue: number;
    avgValue: number;
    medianValue: number;
    totalValue: number;
    clusterWeightedAvg: number | null;
    probabilityWeightedExpectedValue: number | null;
  } | null;
  histogram: HistogramBucket[];
  tiers: TierValueStats[];
}

type SortOption = "value" | "name" | "recent";
type SortOrder = "asc" | "desc";

function formatTimeAgo(date: Date | string | null): string {
  if (!date) return "Never";
  const now = new Date();
  const d = new Date(date);
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}

export function InventoryPage() {
  const { selectedMachine } = useAppContext();
  const queryClient = useQueryClient();
  const [selectedTierId, setSelectedTierId] = useState<number | "all">("all");
  const [sortBy, setSortBy] = useState<SortOption>("value");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [showChanges, setShowChanges] = useState(false);
  const [showValueStats, setShowValueStats] = useState(false);
  const [showTierBreakdown, setShowTierBreakdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // State for histogram mobile click support
  const [selectedHistogramBar, setSelectedHistogramBar] = useState<{
    type: "overall" | "tier";
    tierId?: number;
    barIndex: number;
  } | null>(null);
  const histogramRef = useRef<HTMLDivElement>(null);

  // Close histogram tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (histogramRef.current && !histogramRef.current.contains(event.target as Node)) {
        setSelectedHistogramBar(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Fetch machine info to get tiers
  const { data: machineData } = useQuery({
    queryKey: ["machine-stats", selectedMachine],
    queryFn: async () => {
      if (!selectedMachine) return null;
      const response = await api.stats.machines({ identifier: selectedMachine }).get();
      if (response.data && "machine" in response.data) {
        return response.data as MachineStatsResponse;
      }
      return null;
    },
    enabled: !!selectedMachine,
    staleTime: 60000,
  });

  // Sort tiers by displayOrder
  const sortedTiers = useMemo(() => {
    if (!machineData?.tiers) return [];
    return [...machineData.tiers].sort((a, b) => a.displayOrder - b.displayOrder);
  }, [machineData?.tiers]);

  // Debounced search query for API calls (avoid too many requests while typing)
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch inventory with pagination
  // Each tier/search selection is cached independently for 5 minutes
  const {
    data: inventoryData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: ["inventory", selectedMachine, sortBy, sortOrder, selectedTierId, debouncedSearch],
    queryFn: async ({ pageParam }) => {
      if (!selectedMachine) return null;
      const query: Record<string, string> = {
        limit: "20",
        sort: sortBy,
        order: sortOrder,
      };
      if (pageParam) query.cursor = pageParam;
      // Send tier filter to server (if not "all")
      if (selectedTierId !== "all") {
        query.tier = String(selectedTierId);
      }
      // Send search filter to server
      if (debouncedSearch.trim()) {
        query.search = debouncedSearch.trim();
      }

      const response = await api.inventory.machines({ identifier: selectedMachine }).get({ query });

      if (response.data && "items" in response.data) {
        return response.data as InventoryResponse;
      }
      throw new Error("Failed to fetch inventory");
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage?.pagination?.hasMore ? lastPage.pagination.nextCursor : undefined,
    enabled: !!selectedMachine,
    staleTime: 300000, // 5 minutes - each tier cached independently
    gcTime: 600000, // 10 minutes - keep in cache longer
  });

  // Fetch recent changes
  const { data: changesData } = useQuery({
    queryKey: ["inventory-changes", selectedMachine],
    queryFn: async () => {
      if (!selectedMachine) return null;
      const response = await api.inventory
        .machines({ identifier: selectedMachine })
        .changes.get({ query: { limit: "20" } });

      if (response.data && "changes" in response.data) {
        return response.data as ChangesResponse;
      }
      return null;
    },
    enabled: !!selectedMachine && showChanges,
    staleTime: 60000,
  });

  // Fetch value distribution stats
  const { data: valueStats } = useQuery({
    queryKey: ["inventory-stats", selectedMachine],
    queryFn: async () => {
      if (!selectedMachine) return null;
      const response = await api.inventory
        .machines({ identifier: selectedMachine })
        .stats.get();

      if (response.data && "overall" in response.data) {
        return response.data as ValueStatsResponse;
      }
      return null;
    },
    enabled: !!selectedMachine && showValueStats,
    staleTime: 300000, // 5 minutes
  });

  // Refresh mutation
  const refreshMutation = useMutation({
    mutationFn: async () => {
      if (!selectedMachine) throw new Error("No machine selected");
      const response = await api.inventory
        .machines({ identifier: selectedMachine })
        .refresh.post();
      return response.data;
    },
    onSuccess: () => {
      // Invalidate inventory queries after a short delay to allow sync to start
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["inventory", selectedMachine] });
      }, 2000);
    },
  });

  const allItems = inventoryData?.pages.flatMap((p) => p?.items ?? []) ?? [];
  const syncStatus = inventoryData?.pages[0]?.syncStatus ?? null;
  const tierCounts = inventoryData?.pages[0]?.tierCounts ?? {};
  const totalItems = inventoryData?.pages[0]?.pagination?.total ?? 0;

  // Check if sync is in progress or never completed
  const isSyncing = syncStatus?.lastSyncStatus === "syncing";
  const hasNeverSynced = syncStatus === null || syncStatus.lastSyncedAt === null;
  const showSyncingState = isSyncing || hasNeverSynced;

  // Check if refresh is rate limited
  const isRateLimited =
    refreshMutation.data &&
    "status" in refreshMutation.data &&
    refreshMutation.data.status === "rate_limited";

  // No machine selected
  if (!selectedMachine) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Card className="max-w-md">
          <CardContent className="py-8 text-center">
            <Info className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-lg font-semibold mb-2">No Machine Selected</h2>
            <p className="text-muted-foreground">
              Select a machine from the dropdown to view its inventory.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Card className="max-w-md border-destructive/50">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 mx-auto text-destructive mb-4" />
            <h2 className="text-lg font-semibold mb-2">Failed to Load Inventory</h2>
            <p className="text-muted-foreground">
              {error instanceof Error ? error.message : "An unexpected error occurred"}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Package className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle>Inventory</CardTitle>
                <CardDescription>
                  Current prize pool for {machineData?.machine.name ?? selectedMachine}
                </CardDescription>
              </div>
            </div>

            {/* Sync Status & Refresh */}
            <div className="flex items-center gap-3">
              {isSyncing ? (
                <Badge variant="secondary" className="gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Syncing...
                </Badge>
              ) : syncStatus?.lastSyncedAt ? (
                <div className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  <span>Synced {formatTimeAgo(syncStatus.lastSyncedAt)}</span>
                </div>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={() => refreshMutation.mutate()}
                disabled={refreshMutation.isPending || !!isRateLimited || isSyncing}
              >
                <RefreshCw
                  className={cn(
                    "w-4 h-4 mr-2",
                    refreshMutation.isPending && "animate-spin"
                  )}
                />
                {refreshMutation.isPending ? "Refreshing..." : "Refresh"}
              </Button>
            </div>
          </div>

          {/* Filters Row - hide when syncing */}
          {!showSyncingState && (
          <div className="mt-4 pt-4 border-t border-border space-y-3">
            {/* Tier Filter */}
            {sortedTiers.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-muted-foreground mr-2">Filter by tier:</span>
                <button
                  onClick={() => setSelectedTierId("all")}
                  className={cn(
                    "px-3 py-1.5 rounded-full text-xs font-medium transition-colors border",
                    selectedTierId === "all"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                  )}
                >
                  All ({totalItems})
                </button>
                {sortedTiers.map((tier) => {
                  const count = tierCounts[tier.name.toLowerCase()] ?? 0;
                  const isSelected = selectedTierId === tier.id;
                  return (
                    <button
                      key={tier.id}
                      onClick={() => setSelectedTierId(tier.id)}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-xs font-medium transition-colors border",
                        isSelected
                          ? "border-transparent text-white"
                          : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30"
                      )}
                      style={isSelected ? { backgroundColor: tier.color } : undefined}
                    >
                      {tier.name} ({count})
                    </button>
                  );
                })}
              </div>
            )}

            {/* Search Box */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-secondary/50 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              />
            </div>

            {/* Sort & Changes Toggle */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Sort:</span>
                <div className="flex items-center gap-1">
                  {(["value", "name", "recent"] as const).map((option) => (
                    <button
                      key={option}
                      onClick={() => setSortBy(option)}
                      className={cn(
                        "px-2.5 py-1 rounded text-xs font-medium transition-colors",
                        sortBy === option
                          ? "bg-secondary text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {option === "value" ? "Value" : option === "name" ? "Name" : "Recent"}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
                  className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors text-muted-foreground hover:text-foreground hover:bg-secondary"
                  title={sortOrder === "desc" ? "Highest first" : "Lowest first"}
                >
                  <ArrowUpDown className="w-3.5 h-3.5" />
                  {sortOrder === "desc" ? "High-Low" : "Low-High"}
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowValueStats(!showValueStats)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors",
                    showValueStats
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <BarChart3 className="w-3.5 h-3.5" />
                  Value Insights
                </button>
                <button
                  onClick={() => setShowChanges(!showChanges)}
                  className={cn(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors",
                    showChanges
                      ? "bg-secondary text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <History className="w-3.5 h-3.5" />
                  Recent Changes
                </button>
              </div>
            </div>
          </div>
          )}
        </CardHeader>

        <CardContent>
          {/* Syncing State - show when initial sync is in progress */}
          {showSyncingState && !isLoading && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="p-4 bg-primary/10 rounded-full mb-4">
                <Loader2 className="h-12 w-12 text-primary animate-spin" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {isSyncing ? "Syncing Inventory..." : "Waiting for Initial Sync"}
              </h3>
              <p className="text-muted-foreground max-w-md">
                {isSyncing
                  ? "Fetching the latest inventory data from the machine. This may take a moment."
                  : "Inventory data is being synchronized. Please wait for the initial sync to complete."}
              </p>
            </div>
          )}

          {/* Value Distribution Insights Panel */}
          {!showSyncingState && showValueStats && valueStats?.overall && (
            <div className="mb-6 p-4 rounded-lg border border-border bg-secondary/30" ref={histogramRef}>
              <h4 className="text-sm font-medium mb-4 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Value Distribution Insights
              </h4>

              {/* Overall Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-3 mb-4">
                <div className="text-center p-2 rounded bg-background/50">
                  <p className="text-xs text-muted-foreground">Min Value</p>
                  <p className="text-sm font-semibold text-red-400">
                    ${valueStats.overall.minValue.toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-2 rounded bg-background/50">
                  <p className="text-xs text-muted-foreground">Max Value</p>
                  <p className="text-sm font-semibold text-emerald-400">
                    ${valueStats.overall.maxValue.toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-2 rounded bg-background/50">
                  <p className="text-xs text-muted-foreground">Average</p>
                  <p className="text-sm font-semibold">
                    ${Math.round(valueStats.overall.avgValue).toLocaleString()}
                  </p>
                </div>
                <div
                  className="text-center p-2 rounded bg-background/50 cursor-help"
                  title="Density-weighted average: Items in high-density value ranges get more weight, pulling the average toward where most items cluster. Unlike simple average, outliers have less influence."
                >
                  <p className="text-xs text-muted-foreground">Cluster Avg</p>
                  <p className="text-sm font-semibold text-amber-400">
                    {valueStats.overall.clusterWeightedAvg != null
                      ? `$${Math.round(valueStats.overall.clusterWeightedAvg).toLocaleString()}`
                      : "-"}
                  </p>
                </div>
                {valueStats.overall.probabilityWeightedExpectedValue != null && (
                  <div
                    className="text-center p-2 rounded bg-background/50 cursor-help"
                    title="Probability-weighted expected value: Each tier's average value is weighted by its configured win probability. This represents the expected value per win based on tier odds."
                  >
                    <p className="text-xs text-muted-foreground">Expected Value</p>
                    <p className="text-sm font-semibold text-purple-400">
                      ${Math.round(valueStats.overall.probabilityWeightedExpectedValue).toLocaleString()}
                    </p>
                  </div>
                )}
                <div className="text-center p-2 rounded bg-background/50">
                  <p className="text-xs text-muted-foreground">Median</p>
                  <p className="text-sm font-semibold">
                    ${Math.round(valueStats.overall.medianValue).toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-2 rounded bg-background/50">
                  <p className="text-xs text-muted-foreground">Total Value</p>
                  <p className="text-sm font-semibold">
                    ${valueStats.overall.totalValue.toLocaleString()}
                  </p>
                </div>
                <div className="text-center p-2 rounded bg-background/50">
                  <p className="text-xs text-muted-foreground">Items</p>
                  <p className="text-sm font-semibold">{valueStats.overall.count}</p>
                </div>
              </div>

              {/* Combined Histogram - shows all tiers with their colors */}
              {/* Heights are normalized per-tier so each tier's distribution is visible */}
              {valueStats.histogram.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs text-muted-foreground font-medium mb-2">Value Distribution (All Tiers)</p>
                  {(() => {
                    // Calculate max count per tier for normalized heights
                    const maxCountPerTier: Record<string, number> = {};
                    for (const bucket of valueStats.histogram) {
                      const tierName = bucket.tierName ?? "unknown";
                      maxCountPerTier[tierName] = Math.max(maxCountPerTier[tierName] ?? 0, bucket.count);
                    }

                    // Track tier boundaries for labels
                    let lastTierName: string | null = null;
                    const tierBoundaries: Array<{ index: number; min: number; max: number; color: string; isFirst: boolean; isLast: boolean }> = [];
                    valueStats.histogram.forEach((bucket, i) => {
                      const tierName = bucket.tierName ?? "unknown";
                      const isFirstOfTier = tierName !== lastTierName;
                      const isLastBar = i === valueStats.histogram.length - 1;
                      if (isFirstOfTier) {
                        tierBoundaries.push({ index: i, min: bucket.min, max: bucket.max, color: bucket.tierColor ?? "", isFirst: true, isLast: false });
                      }
                      if (isLastBar) {
                        tierBoundaries.push({ index: i, min: bucket.min, max: bucket.max, color: bucket.tierColor ?? "", isFirst: false, isLast: true });
                      }
                      lastTierName = tierName;
                    });

                    return (
                      <>
                        {/* Bars */}
                        <div className="flex items-end gap-0.5 h-24 bg-background/30 rounded p-2">
                          {valueStats.histogram.map((bucket, i) => {
                            const tierName = bucket.tierName ?? "unknown";
                            const tierMax = maxCountPerTier[tierName] ?? 1;
                            const heightPercent = tierMax > 0 ? (bucket.count / tierMax) * 100 : 0;
                            const isSelected = selectedHistogramBar?.type === "overall" && selectedHistogramBar.barIndex === i;

                            return (
                              <div
                                key={i}
                                className="flex-1 h-full flex flex-col justify-end group relative"
                                onClick={() => setSelectedHistogramBar(isSelected ? null : { type: "overall", barIndex: i })}
                              >
                                <div
                                  className={cn(
                                    "w-full rounded-t transition-colors cursor-pointer hover:opacity-100",
                                    bucket.count === 0 ? "opacity-20" : "opacity-70",
                                    isSelected && "ring-2 ring-white/50 opacity-100"
                                  )}
                                  style={{
                                    height: `${Math.max(heightPercent, bucket.count > 0 ? 4 : 2)}%`,
                                    backgroundColor: bucket.tierColor ?? "hsl(var(--primary))",
                                  }}
                                  title={`${bucket.tierName}: ${bucket.label} - ${bucket.count} items`}
                                />
                                {/* Tooltip - show on hover (desktop) or when selected (mobile) */}
                                <div className={cn(
                                  "absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-10 pointer-events-none",
                                  isSelected ? "block" : "hidden group-hover:block"
                                )}>
                                  <div className="bg-popover text-popover-foreground text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap border border-border">
                                    <span className="font-medium" style={{ color: bucket.tierColor }}>{bucket.tierName}</span>
                                    <br />
                                    {bucket.label}
                                    <br />
                                    {bucket.count} items
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Labels row - separate from chart, px-2 to match bar container padding */}
                        <div className="flex gap-0.5 h-7 sm:h-auto mt-1 px-2 overflow-hidden">
                          {valueStats.histogram.map((bucket, i) => (
                            <div key={i} className="flex-1 flex justify-center min-w-0 overflow-hidden">
                              <span className="text-[11px] sm:text-xs font-medium text-muted-foreground [writing-mode:vertical-lr] sm:[writing-mode:horizontal-tb] rotate-180 sm:rotate-0 text-right sm:text-center">
                                {bucket.count}
                              </span>
                            </div>
                          ))}
                        </div>

                        {/* Tier boundary labels row */}
                        {(() => {
                          const midIndex = Math.floor(valueStats.histogram.length / 2);
                          const midBucket = valueStats.histogram[midIndex];
                          return (
                            <div className="flex justify-between text-[11px] sm:text-xs text-muted-foreground mt-0.5 px-2">
                              <span style={{ color: valueStats.histogram[0]?.tierColor }}>
                                ${valueStats.histogram[0]?.min.toLocaleString()}
                              </span>
                              <span style={{ color: midBucket?.tierColor }}>
                                ${midBucket?.min.toLocaleString()}
                              </span>
                              <span style={{ color: valueStats.histogram[valueStats.histogram.length - 1]?.tierColor }}>
                                ${valueStats.histogram[valueStats.histogram.length - 1]?.max.toLocaleString()}
                              </span>
                            </div>
                          );
                        })()}
                      </>
                    );
                  })()}
                </div>
              )}

              {/* Per-Tier Stats (Collapsible) */}
              {valueStats.tiers.length > 0 && (
                <div className="space-y-2">
                  <button
                    onClick={() => setShowTierBreakdown(!showTierBreakdown)}
                    className="flex items-center gap-2 text-xs text-muted-foreground font-medium hover:text-foreground transition-colors w-full"
                  >
                    <ChevronDown
                      className={cn(
                        "w-4 h-4 transition-transform",
                        showTierBreakdown && "rotate-180"
                      )}
                    />
                    Per Tier Breakdown
                    <span className="text-muted-foreground/60">
                      ({valueStats.tiers.filter((t) => t.count > 0).length} tiers)
                    </span>
                  </button>
                  {showTierBreakdown && (
                    <div className="grid gap-3 pt-2">
                      {valueStats.tiers
                        .filter((tier) => tier.count > 0)
                        .map((tier) => {
                          const tierMaxCount = tier.histogram.length > 0
                            ? Math.max(...tier.histogram.map((b) => b.count))
                            : 0;
                          return (
                            <div
                              key={tier.tierId}
                              className="p-3 rounded bg-background/50"
                            >
                              {/* Tier Header */}
                              <div className="flex items-center gap-2 mb-2">
                                <div
                                  className="w-3 h-3 rounded-full shrink-0"
                                  style={{ backgroundColor: tier.tierColor }}
                                />
                                <span className="text-sm font-medium">{tier.tierName}</span>
                                <span className="text-xs text-muted-foreground">
                                  ({tier.count} items, {tier.percentOfTotal.toFixed(1)}%)
                                </span>
                              </div>

                              {/* Tier Stats Row */}
                              <div className="grid grid-cols-2 sm:grid-cols-6 gap-x-3 gap-y-1 text-xs mb-2">
                                <div>
                                  <span className="text-muted-foreground">Min: </span>
                                  <span className="text-red-400">
                                    ${tier.minValue?.toLocaleString() ?? "-"}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Max: </span>
                                  <span className="text-emerald-400">
                                    ${tier.maxValue?.toLocaleString() ?? "-"}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Avg: </span>
                                  <span>
                                    ${tier.avgValue ? Math.round(tier.avgValue).toLocaleString() : "-"}
                                  </span>
                                </div>
                                <div
                                  className="cursor-help"
                                  title="Density-weighted average: Items in high-density value ranges get more weight, pulling the average toward where most items cluster."
                                >
                                  <span className="text-muted-foreground">Cluster: </span>
                                  <span className="text-amber-400">
                                    {tier.clusterWeightedAvg != null
                                      ? `$${Math.round(tier.clusterWeightedAvg).toLocaleString()}`
                                      : "-"}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Med: </span>
                                  <span>
                                    ${tier.medianValue ? Math.round(tier.medianValue).toLocaleString() : "-"}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Total: </span>
                                  <span>
                                    ${tier.totalValue?.toLocaleString() ?? "-"}
                                  </span>
                                </div>
                              </div>

                              {/* Tier Histogram - fixed 8 buckets */}
                              {tier.histogram.length > 0 && (
                                <div className="mt-2">
                                  {/* Bars */}
                                  <div className="flex items-end gap-0.5 h-12 bg-background/30 rounded p-1">
                                    {tier.histogram.map((bucket, i) => {
                                      const heightPercent = tierMaxCount > 0 ? (bucket.count / tierMaxCount) * 100 : 0;
                                      const isSelected = selectedHistogramBar?.type === "tier" &&
                                        selectedHistogramBar.tierId === tier.tierId &&
                                        selectedHistogramBar.barIndex === i;
                                      return (
                                        <div
                                          key={i}
                                          className="flex-1 h-full flex flex-col justify-end group relative"
                                          onClick={() => setSelectedHistogramBar(isSelected ? null : { type: "tier", tierId: tier.tierId, barIndex: i })}
                                        >
                                          <div
                                            className={cn(
                                              "w-full rounded-t transition-colors cursor-pointer hover:opacity-100",
                                              bucket.count === 0 ? "opacity-20" : "opacity-70",
                                              isSelected && "ring-2 ring-white/50 opacity-100"
                                            )}
                                            style={{
                                              height: `${Math.max(heightPercent, bucket.count > 0 ? 4 : 2)}%`,
                                              backgroundColor: tier.tierColor,
                                            }}
                                            title={`${bucket.label}: ${bucket.count} items`}
                                          />
                                          {/* Tooltip - show on hover (desktop) or when selected (mobile) */}
                                          <div className={cn(
                                            "absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-10 pointer-events-none",
                                            isSelected ? "block" : "hidden group-hover:block"
                                          )}>
                                            <div className="bg-popover text-popover-foreground text-[10px] px-1.5 py-0.5 rounded shadow-lg whitespace-nowrap border border-border">
                                              {bucket.label}: {bucket.count}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {/* Labels row - separate from chart, px-1 to match bar container padding */}
                                  <div className="flex gap-0.5 h-6 sm:h-auto mt-1 px-1 overflow-hidden">
                                    {tier.histogram.map((bucket, i) => (
                                      <div key={i} className="flex-1 flex justify-center min-w-0 overflow-hidden">
                                        <span className="text-[10px] sm:text-[11px] font-medium text-muted-foreground [writing-mode:vertical-lr] sm:[writing-mode:horizontal-tb] rotate-180 sm:rotate-0 text-right sm:text-center">
                                          {bucket.count}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                  {/* X-axis labels */}
                                  <div className="flex justify-between text-[11px] sm:text-xs text-muted-foreground mt-0.5 px-1">
                                    <span>${tier.histogram[0]?.min.toLocaleString()}</span>
                                    <span>${tier.histogram[Math.floor(tier.histogram.length / 2)]?.min.toLocaleString()}</span>
                                    <span>${tier.histogram[tier.histogram.length - 1]?.max.toLocaleString()}</span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Recent Changes Panel */}
          {!showSyncingState && showChanges && changesData && changesData.changes.length > 0 && (
            <div className="mb-6 p-4 rounded-lg border border-border bg-secondary/30">
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                <History className="w-4 h-4" />
                Recent Inventory Changes
              </h4>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {changesData.changes.map((change) => (
                  <div
                    key={change.id}
                    className="flex items-center gap-3 text-sm py-1"
                  >
                    <div
                      className={cn(
                        "w-5 h-5 rounded-full flex items-center justify-center shrink-0",
                        change.changeType === "added"
                          ? "bg-emerald-500/20 text-emerald-400"
                          : "bg-red-500/20 text-red-400"
                      )}
                    >
                      {change.changeType === "added" ? (
                        <Plus className="w-3 h-3" />
                      ) : (
                        <Minus className="w-3 h-3" />
                      )}
                    </div>
                    <span className="truncate flex-1">{change.name}</span>
                    {change.insuredValue !== null && (
                      <span className="text-muted-foreground shrink-0">
                        ${change.insuredValue.toLocaleString()}
                      </span>
                    )}
                    {change.tier && (
                      <Badge
                        variant="outline"
                        className="shrink-0 text-xs"
                        style={{
                          borderColor: change.tier.tierColor,
                          color: change.tier.tierColor,
                        }}
                      >
                        {change.tier.tierName}
                      </Badge>
                    )}
                    <span className="text-muted-foreground text-xs shrink-0">
                      {formatTimeAgo(change.detectedAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inventory Grid - only show when not in syncing state */}
          {!showSyncingState && (
            <>
              {isLoading && allItems.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                </div>
              ) : allItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="p-4 bg-secondary rounded-full mb-4">
                    <Package className="h-12 w-12 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">No Items Found</h3>
                  <p className="text-muted-foreground max-w-md">
                    {searchQuery ? "No items match your search." : "No inventory items match the current filter."}
                  </p>
                </div>
              ) : (
                <>
                  {/* Grid of Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                    {allItems.map((item) => (
                      <div
                        key={item.id}
                        className="group relative bg-card border border-border rounded-lg overflow-hidden hover:border-foreground/20 transition-colors"
                      >
                        {/* Image */}
                        <div className="aspect-square bg-secondary relative">
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                          {/* Tier Badge Overlay */}
                          {item.tier && (
                            <div
                              className="absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] font-semibold text-white shadow"
                              style={{ backgroundColor: item.tier.tierColor }}
                            >
                              {item.tier.tierName}
                            </div>
                          )}
                          {/* CollectorCrypt Link Overlay */}
                          {item.nftMint && (
                            <a
                              href={`https://collectorcrypt.com/assets/solana/${item.nftMint}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="absolute top-2 left-2 p-1.5 bg-black/60 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/80"
                              title="View on CollectorCrypt"
                            >
                              <ExternalLink className="w-3 h-3 text-white" />
                            </a>
                          )}
                        </div>

                        {/* Info */}
                        <div className="p-2">
                          <h4 className="text-xs font-medium truncate" title={item.name}>
                            {item.name}
                          </h4>
                          <div className="flex items-center justify-between mt-1">
                            {item.insuredValue !== null ? (
                              <span className="text-sm font-semibold text-emerald-400">
                                ${item.insuredValue.toLocaleString()}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                            {item.gradingCompany && item.grade && (
                              <span className="text-[10px] text-muted-foreground">
                                {item.gradingCompany} {item.grade}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Load More */}
                  {hasNextPage && (
                    <div className="flex justify-center pt-6">
                      <Button
                        variant="outline"
                        onClick={() => fetchNextPage()}
                        disabled={isFetchingNextPage}
                      >
                        {isFetchingNextPage ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Loading...
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4 mr-2" />
                            Load More
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats - hide when syncing, show all tiers */}
      {!showSyncingState && totalItems > 0 && (
        <div className={cn(
          "grid gap-3 md:gap-4",
          sortedTiers.length <= 3
            ? "grid-cols-2 md:grid-cols-4"
            : "grid-cols-2 sm:grid-cols-3 md:grid-cols-5"
        )}>
          <Card>
            <CardContent className="py-4">
              <p className="text-xs md:text-sm text-muted-foreground">Total Items</p>
              <p className="text-xl md:text-2xl font-bold">{totalItems}</p>
            </CardContent>
          </Card>
          {sortedTiers.map((tier) => {
            const count = tierCounts[tier.name.toLowerCase()] ?? 0;
            return (
              <Card key={tier.id}>
                <CardContent className="py-4">
                  <p
                    className="text-xs md:text-sm font-medium"
                    style={{ color: tier.color }}
                  >
                    {tier.name}
                  </p>
                  <p className="text-xl md:text-2xl font-bold">{count}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
